import Papa from "papaparse";
import {loadExcelJS} from "@/helpers/loadExcelJS";

const HELP_PREFIX = '#';
const CSV_SHEET = 'Products';

export class ImportFileError extends Error {
    constructor(code) {
        super(code);
        this.code = code;
    }
}

function extensionOf(file) {
    const name = (file?.name || '').toLowerCase();
    return name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : '';
}

function cellContent(value) {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'object') {
        if (Array.isArray(value.richText)) return value.richText.map(part => part.text).join('');
        if ('result' in value) return cellContent(value.result);
        if ('text' in value) return cellContent(value.text);
        return '';
    }
    return value;
}

function isBlank(value) {
    return value === '' || value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

function isHelpRow(cells) {
    const filled = cells.filter(cell => !isBlank(cell));
    return filled.length > 0 && filled.every(cell => typeof cell === 'string' && cell.trim().startsWith(HELP_PREFIX));
}

function toSheet(name, lines) {
    const meaningful = lines.filter(line => line.cells.some(cell => !isBlank(cell)) && !isHelpRow(line.cells));
    const [header, ...rows] = meaningful;
    return {
        name,
        header: header ? header.cells.map(cell => String(cell ?? '').trim()) : [],
        rows,
    };
}

async function readWorkbook(file) {
    const ExcelJS = await loadExcelJS();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());
    const sheets = [];
    workbook.eachSheet(worksheet => {
        const lines = [];
        worksheet.eachRow({includeEmpty: false}, (row, rowNumber) => {
            const values = Array.isArray(row.values) ? row.values.slice(1) : [];
            lines.push({rowNumber, cells: Array.from(values, cellContent)});
        });
        sheets.push(toSheet(worksheet.name, lines));
    });
    return sheets;
}

async function readText(file) {
    const buffer = await file.arrayBuffer();
    const text = new TextDecoder('utf-8').decode(buffer);
    return text.includes('\uFFFD') ? new TextDecoder('windows-1252').decode(buffer) : text;
}

async function readCsv(file) {
    const text = (await readText(file)).replace(/^\uFEFF/, '');
    const {data} = Papa.parse(text, {skipEmptyLines: 'greedy'});
    const lines = data.map((cells, index) => ({rowNumber: index + 1, cells}));
    return [toSheet(CSV_SHEET, lines)];
}

export async function readImportFile(file) {
    const extension = extensionOf(file);
    try {
        if (extension === 'xlsx') return {format: 'xlsx', sheets: await readWorkbook(file)};
        if (extension === 'csv') return {format: 'csv', sheets: await readCsv(file)};
    } catch (error) {
        console.error(error);
        throw new ImportFileError('readError');
    }
    throw new ImportFileError('unsupportedFormat');
}
